/**
 * Dashboard — a customizable overview. Pulls the metric sources once and lays
 * out widgets the client can add / hide / drag-reorder (Spec 11). Layout
 * persists per browser. Each widget is defined in lib/dashboard-widgets.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useSetupGate, SetupLocked, PullingData, SyncError } from "../lib/setup-gate.js";
import { useDrill } from "../lib/drill.js";
import { usePeriod } from "../lib/period.js";
import { useDemoMode } from "../lib/demo-mode.js";
import { useWidgetCtx } from "../lib/use-widget-ctx.js";
import { InsightLine, type MoverRow } from "../components/ExecStory.js";
import { CardLibrary } from "../components/CardLibrary.js";
import type { DrillWindow } from "../components/DrillExplorer.js";
import { WIDGETS, DEFAULT_LAYOUT, type WidgetDef } from "../lib/dashboard-widgets.js";
import { CardAnchorCtx } from "../lib/card-anchor.js";
import { useUiPref } from "../lib/ui-prefs.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { currentPeriodData } from "../lib/period-query-data.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

const SPAN: Record<string, number> = { kpi: 3, half: 6, wide: 12 };
// The Exec card order now persists to the USER'S ACCOUNT (cross-device) via
// useUiPref, not just localStorage (founder 2026-06-30). The "v7" in the key is
// the default-layout version — bump it when DEFAULT_LAYOUT changes so saved
// layouts re-default instead of masking the change. v7 (2026-06-21): founder
// declutter — tight profit-first default. Stale ids are filtered at render.
const LAYOUT_PREF_KEY = "dash-layout-v8";

export const GROSS_SAFE_EXEC_WIDGET_IDS: ReadonlySet<string> = new Set([
  "revenue",
  "orders",
  "customers",
  "repeat",
  "refund",
  "revenue-trend",
  "top-products",
  "time-between",
]);

export function execWidgetIdsForBasis(
  layout: string[],
  financialBasis: Capabilities["financialBasis"] | undefined,
): string[] {
  if (financialBasis === "profit") return layout;
  const safe = layout.filter((id) => GROSS_SAFE_EXEC_WIDGET_IDS.has(id));
  if (safe.length > 0) return safe;
  return DEFAULT_LAYOUT.filter((id) => GROSS_SAFE_EXEC_WIDGET_IDS.has(id));
}

export function canAddExecWidget(
  widgetId: string,
  financialBasis: Capabilities["financialBasis"] | undefined,
): boolean {
  return financialBasis === "profit" || GROSS_SAFE_EXEC_WIDGET_IDS.has(widgetId);
}

export default function DashboardPage(): React.ReactElement {
  const api = useApiClient();
  const gate = useSetupGate();
  const navigate = useNavigate();
  const drill = useDrill();
  const period = usePeriod();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  const widgetData = useWidgetCtx(ready);
  const ctx = widgetData.ctx;
  const demo = useDemoMode();
  const [editing, setEditing] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [layout, setLayout] = useUiPref<string[]>(LAYOUT_PREF_KEY, DEFAULT_LAYOUT);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  // Product movers vs the prior window — feeds the "what changed" insight line.
  const movers = useQuery({
    queryKey: ["dash-movers", period.cacheKey],
    enabled: ready && period.key !== "all",
    queryFn: () =>
      api.get<{ rows: MoverRow[] }>("/dashboard/breakdown", { metric: "revenue", dimension: "product", ...period.query, compare: "prior" }),
  });
  const moverRows = currentPeriodData(movers.data, movers.isPlaceholderData)?.rows;
  const gateLoad = pageLoadState(gate);

  if (gateLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your dashboard numbers"
        retrying={gateLoad.retrying}
        onRetry={gateLoad.retry}
      />
    );
  }
  if (gate.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (gate.data?.locked) return <SetupLocked />;
  if (gate.data?.syncStatus === "error") return <SyncError message={gate.data.errorMessage} />;
  if (gate.data?.syncStatus === "pending") return <PullingData />;
  if (widgetData.loadFailed) {
    return (
      <PageLoadError
        title="We couldn't load your dashboard numbers"
        retrying={widgetData.retrying}
        onRetry={widgetData.retry}
      />
    );
  }

  const effectiveBasis = demo ? "profit" : ctx.caps?.financialBasis;
  const revenuePhase = effectiveBasis !== "profit";
  const availableWidgets = revenuePhase ? WIDGETS.filter((widget) => GROSS_SAFE_EXEC_WIDGET_IDS.has(widget.id)) : WIDGETS;
  const visible = execWidgetIdsForBasis(layout, effectiveBasis)
    .map((id) => WIDGETS.find((w) => w.id === id))
    .filter(Boolean) as WidgetDef[];
  const hidden = availableWidgets.filter((w) => !layout.includes(w.id));

  const reorder = (overId: string): void => {
    if (!dragId || dragId === overId) return;
    const from = layout.indexOf(dragId);
    const to = layout.indexOf(overId);
    if (from < 0 || to < 0) return;
    const next = layout.filter((id) => id !== dragId);
    const at = next.indexOf(overId);
    // Dragging DOWN (from before the target) drops AFTER it; dragging UP drops
    // BEFORE it — so a card lands where you released it in both directions.
    next.splice(from < to ? at + 1 : at, 0, dragId);
    setLayout(next);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{revenuePhase ? "Exec · gross results" : "Exec · profit model"}</div>
          <h1 style={{ margin: "4px 0 0", fontSize: 27 }}>How's the business?</h1>
          <InsightLine
            revenuePct={ctx.overview?.changes.revenuePct}
            periodKey={period.key}
            movers={moverRows}
            onMoverClick={(product) => {
              // The mover is a DELTA vs the prior period — a product that died
              // this period has zero rows in the current window (founder bug
              // 2026-06-12: "drill shows nothing"). Open across BOTH periods.
              const days = period.windowDays;
              const win: DrillWindow = days
                ? { from: new Date(Date.now() - 2 * days * 86_400_000).toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10), label: `last ${2 * days} days (this period + prior)` }
                : { key: "all", label: "All history" };
              drill.open({
                title: `${product} — this period vs prior`,
                metric: "revenue",
                dimension: "month",
                period: win,
                filters: { product },
              });
            }}
          />
          {gate.data?.lastSyncedAt && (
            <span style={{ color: "var(--body)", fontSize: 12 }}>Last updated: {new Date(gate.data.lastSyncedAt).toLocaleString()}</span>
          )}
          {/* Subtle client trust line — renders ONLY when the latest verify run
              is clean (server returns null on any red or before the first run),
              so it can never advertise while the numbers are broken. */}
          {!demo && <TrustLine enabled={ready} />}
        </div>
        {!demo && (
          <div style={{ display: "flex", gap: 10 }}>
            {editing && (
              <button onClick={() => setLayout(DEFAULT_LAYOUT)} style={btn(false)}>Reset</button>
            )}
            {!revenuePhase && <button onClick={() => setLibraryOpen(true)} style={btn(false)}>▦ Card library</button>}
            <button onClick={() => setEditing((e) => !e)} style={btn(editing)}>{editing ? "✓ Done" : "✎ Customize"}</button>
          </div>
        )}
      </div>

      {editing && (
        <div style={{ marginTop: 14, padding: "12px 14px", background: "var(--surface)", border: "1px dashed var(--border)", borderRadius: "var(--r-md)", display: "flex", flexDirection: "column", gap: 9 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span style={{ color: "var(--body)", fontSize: 12.5 }}>
              <b>Customizing.</b> Drag any card to move it. Click <b>✕</b> on a card to hide it. Click <b>✓ Done</b> when finished.
            </span>
            {!revenuePhase && <button onClick={() => setLibraryOpen(true)} style={addChip}>▦ Card library</button>}
          </div>
          {hidden.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ color: "var(--body-faint)", fontSize: 11.5 }}>Hidden — click to add back:</span>
              {hidden.map((w) => (
                <button key={w.id} onClick={() => setLayout([...layout, w.id])} title={`Add ${w.title} back`}
                  style={{ border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", fontSize: 11.5, borderRadius: 999, padding: "3px 10px", cursor: "pointer" }}>
                  + {w.title}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* "N products selling without costs" banner REMOVED (founder 2026-07-03):
          the AppLayout CostReadinessBanner is the one cost nag; the per-card
          indicators cover the rest. Backend /dashboard/action-items stays for
          future use. */}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 16, marginTop: 18 }}>
        {visible.map((w) => (
          // data-card-anchor = the "See it live →" scroll/flash target; the
          // CardAnchorCtx lets this widget's header resolve its rename override.
          <div
            key={w.id}
            data-card-anchor={w.id}
            draggable={editing}
            onDragStart={() => setDragId(w.id)}
            onDragEnd={() => { setDragId(null); setOverId(null); }}
            onDragOver={(e) => { if (editing && dragId) { e.preventDefault(); setOverId(w.id); } }}
            onDrop={() => { reorder(w.id); setDragId(null); setOverId(null); }}
            style={{
              gridColumn: `span ${SPAN[w.size]}`,
              position: "relative",
              opacity: dragId === w.id ? 0.4 : 1,
              cursor: editing ? "grab" : "default",
              outline: editing ? (overId === w.id && dragId !== w.id ? "2px solid var(--accent)" : "1px dashed var(--border)") : "none",
              outlineOffset: 3,
              borderRadius: "var(--r-card)",
            }}
          >
            {editing && (
              <div style={{ position: "absolute", top: 6, right: 6, zIndex: 5 }}>
                <button title="Hide" onClick={() => setLayout(layout.filter((id) => id !== w.id))} style={{ ...editBtn, cursor: "pointer" }}>✕</button>
              </div>
            )}
            <CardAnchorCtx.Provider value={w.id}>{w.render(ctx)}</CardAnchorCtx.Provider>
          </div>
        ))}
      </div>

      {visible.length === 0 && (
        <p style={{ color: "var(--body)", marginTop: 24 }}>No cards yet. Click <b>▦ Card library</b> to add some.</p>
      )}

      {!revenuePhase && <CardLibrary
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          layout={layout}
          onAdd={(id) => {
            if (!canAddExecWidget(id, effectiveBasis)) return;
            setLayout(layout.includes(id) ? layout : [...layout, id]);
          }}
          onRemove={(id) => setLayout(layout.filter((x) => x !== id))}
          caps={ctx.caps}
          onNavigate={navigate}
        />}
    </div>
  );
}

const btn = (on: boolean): React.CSSProperties => ({
  border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
  background: on ? "var(--accent)" : "var(--surface-2)",
  color: on ? "var(--accent-contrast)" : "var(--text)",
  borderRadius: "var(--r-sm)", padding: "7px 13px", fontSize: 13, fontWeight: 600, cursor: "pointer",
});

/** "this morning" before noon on the run day, "today" later that day, else "on
 *  <Mon D>". Plain, human — no time-of-day precision the founder doesn't need. */
function trustWhenPhrase(ranAt: string): string {
  const d = new Date(ranAt);
  const now = new Date();
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return d.getHours() < 12 ? "this morning" : "today";
  return `on ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

/**
 * The client trust line (founder 2026-07-12): a single subtle, non-clickable
 * line with ZERO methodology. The server only returns data when the latest
 * verify run is clean (no red), so this component renders NOTHING while the
 * numbers are broken or before the first run — it never advertises falsely.
 */
function TrustLine({ enabled }: { enabled: boolean }): React.ReactElement | null {
  const api = useApiClient();
  const q = useQuery({
    queryKey: ["verify-trust"],
    enabled,
    queryFn: () => api.get<{ trust: { checksPassed: number; ranAt: string } | null }>("/verify/trust"),
    staleTime: 5 * 60_000,
  });
  const trust = q.data?.trust ?? null;
  if (!trust || trust.checksPassed < 1) return null;
  const n = trust.checksPassed;
  return (
    <div style={{ color: "var(--text-faint)", fontSize: 12, marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
      Your numbers passed {n.toLocaleString()} automatic accuracy check{n === 1 ? "" : "s"} {trustWhenPhrase(trust.ranAt)}
      <span style={{ color: "#16a34a" }} aria-hidden="true">✓</span>
    </div>
  );
}
const addChip: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", borderRadius: 999, padding: "5px 11px", fontSize: 12.5, cursor: "pointer" };
const editBtn: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--body)", fontSize: 12, lineHeight: 1 };
